import { Form } from "@unform/web";
import { ListChecks, Lock, SlidersHorizontal, Trash2, X } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import RegisterFormMenu from "~/components/RegisterForm/Menu";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { toast } from "react-toastify";
import api from "~/services/api";
import { formatter, getRegistries } from "~/functions";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSelector } from "react-redux";
import { format, parseISO } from "date-fns";
import { AlertContext } from "~/App";
import { FullGridContext } from "../../..";
import { BalanceBox, BalanceLine } from "../../components/BalanceLines";
import Step1Filter from "./Step1Filter";
import Step2Selection, { rowName, rowInvoice } from "./Step2Selection";

export const InputContext = createContext({});

function safeDate(value) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM/dd/yyyy");
  } catch (err) {
    return "-";
  }
}

function apiDate(value) {
  if (!value) return null;
  try {
    return format(value, "yyyy-MM-dd");
  } catch (err) {
    return null;
  }
}

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const { alertBox } = useContext(AlertContext);
  const { handleOpened, setSuccessfullyUpdated, successfullyUpdated } =
    useContext(FullGridContext);

  const [conciliationId, setConciliationId] = useState(id);
  const [pageData, setPageData] = useState({ loaded: false, items: [] });
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [formType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(
    id && id !== "new" ? "general" : "filter"
  );

  const [type, setType] = useState("Payable");
  const [filters, setFilters] = useState(null);
  const [available, setAvailable] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const auth = useSelector((state) => state.auth);
  const generalForm = useRef();

  function handleCloseForm() {
    handleOpened(null);
  }

  async function getPageData(targetId = conciliationId) {
    if (!targetId || targetId === "new") {
      setPageData((prev) => ({ ...prev, loaded: true }));
      return;
    }
    try {
      const { data } = await api.get(`/otherconciliations/${targetId}`);
      setPageData((prev) => ({ ...prev, ...data, loaded: true }));

      const {
        created_by,
        created_at,
        updated_by,
        updated_at,
        canceled_by,
        canceled_at,
      } = data;
      setRegistry(
        await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        })
      );
    } catch (err) {
      toast(err?.response?.data?.error || "Could not load the batch.", {
        type: "error",
        autoClose: 3000,
      });
      handleOpened(null);
    }
  }

  useEffect(() => {
    getPageData();
  }, []);

  async function handleFormSubmit(data) {
    if (activeMenu === "selection") {
      await handleCreate();
      return;
    }
    await handleSearchSubmit(data);
  }

  async function handleSearchSubmit(data) {
    const filial_id = data?.filial?.id;
    const date_from = apiDate(data?.date_from);
    const date_to = apiDate(data?.date_to);

    if (!filial_id || !date_from || !date_to) {
      toast("Filial and period are required.", {
        type: "warning",
        autoClose: 2500,
      });
      return;
    }

    const paymentmethod_ids = Array.isArray(data?.paymentmethod_ids)
      ? data.paymentmethod_ids
      : [];

    const payload = {
      filial_id,
      type: data?.type || type,
      date_from,
      date_to,
      conciliation_date: apiDate(data?.conciliation_date),
      paymentmethod_ids,
      expected_amount: Number(String(data?.expected_amount || 0).replace(",", ".")),
      memo: data?.memo || "",
    };

    setLoading(true);
    try {
      const params = {
        filial_id,
        type: payload.type,
        date_from,
        date_to,
      };
      if (paymentmethod_ids.length) {
        params.paymentmethod_ids = paymentmethod_ids.join(",");
      }
      const { data: result } = await api.get("/otherconciliations-available", {
        params,
      });
      const list = Array.isArray(result) ? result : result?.rows || [];
      setFilters(payload);
      setAvailable(list);
      setSelectedIds([]);
      setActiveMenu("selection");
      if (!list.length) {
        toast("No processes found for this filter.", {
          type: "info",
          autoClose: 2500,
        });
      }
    } catch (err) {
      toast(err?.response?.data?.error || "Could not load processes.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }

  function toggleRow(rowId) {
    setSelectedIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((el) => el !== rowId)
        : [...prev, rowId]
    );
  }

  function toggleAllRows() {
    setSelectedIds((prev) =>
      prev.length === available.length ? [] : available.map((row) => row.id)
    );
  }

  async function handleCreate() {
    if (!selectedIds.length) {
      toast("Select at least one process.", {
        type: "warning",
        autoClose: 2000,
      });
      return;
    }
    if (!filters) {
      toast("Run the filter before creating the batch.", {
        type: "warning",
        autoClose: 2500,
      });
      setActiveMenu("filter");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/otherconciliations", {
        ...filters,
        settlement_ids: selectedIds,
      });
      const newId = data?.id;
      if (!newId) {
        toast("The batch was not created — no id returned.", {
          type: "error",
          autoClose: 4000,
        });
        return;
      }
      toast("Batch created!", { autoClose: 1500 });
      setSuccessfullyUpdated(true);
      setConciliationId(newId);
      await getPageData(newId);
    } catch (err) {
      toast(err?.response?.data?.error || "Could not create the batch.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setCreating(false);
    }
  }

  function handleRemoveItem(item) {
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to remove this item from the batch?",
      buttons: [
        { title: "Cancel", class: "cancel" },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(
                `/otherconciliations/${conciliationId}/items/${item.id}`
              );
              toast("Item removed!", { autoClose: 1000 });
              await getPageData();
            } catch (err) {
              toast(
                err?.response?.data?.error || "Could not remove the item.",
                { type: "error", autoClose: 3000 }
              );
            }
          },
        },
      ],
    });
  }

  function handleCloseBatch() {
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to close this batch? It cannot be edited afterwards.",
      buttons: [
        { title: "Cancel", class: "cancel" },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.put(`/otherconciliations/${conciliationId}/close`);
              toast("Batch closed!", { autoClose: 1000 });
              setSuccessfullyUpdated(true);
              await getPageData();
            } catch (err) {
              toast(
                err?.response?.data?.error || "Could not close the batch.",
                { type: "error", autoClose: 3000 }
              );
            }
          },
        },
      ],
    });
  }

  function handleCancelBatch() {
    alertBox({
      title: "Attention!",
      descriptionHTML: "Are you sure you want to cancel this batch?",
      buttons: [
        { title: "Cancel", class: "cancel" },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(`/otherconciliations/${conciliationId}`);
              toast("Batch canceled!", { autoClose: 1000 });
              handleOpened(null);
            } catch (err) {
              toast(err?.response?.data?.error || "Could not cancel.", {
                type: "error",
                autoClose: 3000,
              });
            }
          },
        },
      ],
    });
  }

  const isNew = conciliationId === "new";
  const isClosed = pageData?.status === "Closed";
  const expected = Number(pageData?.expected_amount || 0);
  const total = Number(pageData?.total_amount || 0);
  const hasExpected = Math.abs(expected) >= 0.005;
  const difference =
    pageData?.difference !== undefined
      ? Number(pageData.difference)
      : expected - total;
  const isZero = Math.abs(difference) < 0.005;

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      <div className="flex h-full flex-row items-start justify-between gap-4">
        {isNew ? (
          <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="filter"
            >
              <SlidersHorizontal size={16} /> Filter
            </RegisterFormMenu>
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="selection"
            >
              <ListChecks size={16} /> Selection
            </RegisterFormMenu>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <ListChecks size={16} /> Conciliation
            </RegisterFormMenu>
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="delete"
            >
              <Trash2 size={16} /> Cancel Batch
            </RegisterFormMenu>
          </div>
        )}

        <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
          <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
            <Form
              ref={generalForm}
              onSubmit={handleFormSubmit}
              className="w-full pb-32"
            >
              <InputContext.Provider
                value={{
                  id: conciliationId,
                  generalForm,
                  setSuccessfullyUpdated,
                  fullscreen,
                  setFullscreen,
                  successfullyUpdated,
                  handleCloseForm,
                }}
              >
                {isNew || pageData.loaded ? (
                  <>
                    <FormHeader
                      access={access}
                      title={
                        isNew
                          ? "New Other Conciliation"
                          : `Other Conciliation - ${pageData?.type || ""}`
                      }
                      registry={registry}
                      InputContext={InputContext}
                      className="relative overflow-hidden"
                    />

                    {isNew ? (
                      <>
                        <div
                          className={activeMenu === "filter" ? "" : "hidden"}
                        >
                          <Step1Filter
                            auth={auth}
                            InputContext={InputContext}
                            loading={loading}
                            onTypeChange={(value) => setType(value)}
                          />
                        </div>
                        <div
                          className={activeMenu === "selection" ? "" : "hidden"}
                        >
                          <Step2Selection
                            available={available}
                            selectedIds={selectedIds}
                            onToggle={toggleRow}
                            onToggleAll={toggleAllRows}
                            type={filters?.type || type}
                            expected={Number(filters?.expected_amount || 0)}
                            creating={creating}
                            onBack={() => setActiveMenu("filter")}
                            onCreate={handleCreate}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                      <InputLineGroup
                        title="GENERAL"
                        activeMenu={activeMenu === "general"}
                      >
                        <InputLine title="Information">
                          <div className="flex flex-row flex-wrap items-center gap-2 text-xs text-gray-600 pt-2">
                            <strong>{pageData?.filial?.name || "-"}</strong>
                            <span>·</span>
                            <span>
                              {safeDate(pageData?.date_from)} to{" "}
                              {safeDate(pageData?.date_to)}
                            </span>
                            <span>·</span>
                            <span>
                              Conciliated on{" "}
                              {safeDate(pageData?.conciliation_date)}
                            </span>
                            <span>·</span>
                            <span
                              className={
                                isClosed ? "text-emerald-600" : "text-gray-500"
                              }
                            >
                              {pageData?.status}
                            </span>
                          </div>
                        </InputLine>

                        <InputLine title="Balance">
                          <div className="w-full pt-2">
                            <BalanceBox>
                              {hasExpected ? (
                                <BalanceLine
                                  title="Expected"
                                  value={expected}
                                />
                              ) : null}
                              <BalanceLine title="Selected" value={total} />
                              {hasExpected ? (
                                <BalanceLine
                                  title="Difference"
                                  value={difference}
                                  tone={isZero ? "success" : "danger"}
                                />
                              ) : null}
                              <div className="text-gray-500 pt-1">
                                {pageData?.total_items ??
                                  pageData?.items?.length ??
                                  0}{" "}
                                items
                              </div>
                              {pageData?.paymentMethods?.length ? (
                                <div className="text-gray-500">
                                  Payment Methods:{" "}
                                  {pageData.paymentMethods
                                    .map((m) => m.description)
                                    .join(", ")}
                                </div>
                              ) : null}
                              {pageData?.memo ? (
                                <div className="text-gray-500">
                                  Memo: {pageData.memo}
                                </div>
                              ) : null}
                            </BalanceBox>
                          </div>
                        </InputLine>

                        <table className="w-full text-xs">
                          <thead className="sticky top-0 border-b">
                            <tr className="bg-secondary h-8">
                              <th>Date</th>
                              <th>Name</th>
                              <th>Invoice</th>
                              <th>Payment Method</th>
                              <th>Amount</th>
                              {!isClosed ? (
                                <th className="w-16">Actions</th>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody className="align-center">
                            {pageData?.items?.map((item) => (
                              <tr key={item.id} className="border-b">
                                <td className="py-1">
                                  <div className="flex flex-row items-center justify-center">
                                    {safeDate(item.settlement_date || item.date)}
                                  </div>
                                </td>
                                <td>
                                  <div className="flex flex-row items-center justify-center">
                                    {rowName(item, pageData?.type)}
                                  </div>
                                </td>
                                <td>
                                  <div className="flex flex-row items-center justify-center">
                                    {rowInvoice(item, pageData?.type)}
                                  </div>
                                </td>
                                <td>
                                  <div className="flex flex-row items-center justify-center">
                                    {item.paymentMethod?.description || "-"}
                                  </div>
                                </td>
                                <td>
                                  <div className="flex flex-row items-center justify-center font-bold">
                                    {formatter.format(Number(item.amount || 0))}
                                  </div>
                                </td>
                                {!isClosed ? (
                                  <td>
                                    <div className="flex flex-row items-center justify-center">
                                      <button
                                        type="button"
                                        title="Remove from batch"
                                        onClick={() => handleRemoveItem(item)}
                                        className="hover:scale-125 transition-all text-red-600"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                  </td>
                                ) : null}
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {!isClosed ? (
                          <InputLine title="Actions">
                            <div className="flex flex-row items-center gap-2 pt-2">
                              <button
                                type="button"
                                onClick={handleCloseBatch}
                                className="bg-primary text-white rounded-md p-1 px-2 h-8 flex flex-row items-center justify-center text-xs gap-1 hover:opacity-80 transition-all"
                              >
                                <Lock size={16} /> Close batch
                              </button>
                            </div>
                          </InputLine>
                        ) : null}
                      </InputLineGroup>

                      <InputLineGroup
                        title="CANCEL BATCH"
                        activeMenu={activeMenu === "delete"}
                      >
                        <InputLine title="Cancel Batch" left>
                          <div className="w-full flex flex-col gap-3 pt-2 text-xs">
                            <p>
                              Canceling this batch will undo every conciliation
                              made in it. All records linked to this batch will
                              be released and become available for another
                              batch.
                            </p>
                            <p className="text-red-600 font-bold">
                              This action cannot be undone.
                            </p>
                            <div>
                              <button
                                type="button"
                                onClick={handleCancelBatch}
                                className="border border-red-500 text-red-500 rounded-md p-1 px-2 h-8 flex flex-row items-center justify-center text-xs gap-1 hover:bg-red-500 hover:text-white transition-all"
                              >
                                <Trash2 size={16} /> Cancel batch
                              </button>
                            </div>
                          </div>
                        </InputLine>
                      </InputLineGroup>
                      </>
                    )}
                  </>
                ) : (
                  <FormLoading />
                )}
              </InputContext.Provider>
            </Form>
          </div>
        </div>
      </div>
    </Preview>
  );
}
